import { usePermissions, PermissionCode } from '../contexts/PermissionsContext';

/**
 * Custom hook for permission checks in functional components
 * Provides convenient access to permission checking functions
 * 
 * Usage:
 * const { can, canAny, canAll, isAdmin, isSuperAdmin } = usePermissionCheck();
 * 
 * if (can('demands.create')) {
 *   // Show create button
 * }
 * 
 * if (canAny(['demands.edit', 'demands.assign'])) {
 *   // Show edit or assign button
 * }
 */
export const usePermissionCheck = () => {
  const {
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    isAdmin,
    isSuperAdmin,
    loading,
    permissions,
    refreshPermissions
  } = usePermissions();

  return {
    /**
     * Check if user has a specific permission
     */
    can: (permission: PermissionCode): boolean => {
      return hasPermission(permission);
    },

    /**
     * Check if user has ANY of the specified permissions (OR logic)
     */
    canAny: (permissions: PermissionCode[]): boolean => {
      return hasAnyPermission(permissions);
    },

    /**
     * Check if user has ALL of the specified permissions (AND logic)
     */
    canAll: (permissions: PermissionCode[]): boolean => {
      return hasAllPermissions(permissions);
    },

    /**
     * Check if user has admin-level permissions
     */
    isAdmin,

    /**
     * Check if user has super admin (GOD) level permissions
     */
    isSuperAdmin,

    /**
     * Whether permissions are still loading
     */
    loading,

    /**
     * Get user's roles
     */
    roles: permissions?.roles || [],

    /**
     * Get all user permissions
     */
    permissions: permissions?.permissions || [],

    /**
     * Check if user has a specific role by name
     */
    hasRole: (roleName: string): boolean => {
      return permissions?.roles.some(role => role.nome === roleName) || false;
    },

    /**
     * Re-fetch permissions from the server
     */
    refreshPermissions,
  };
};
